import { Movie } from '../types/Movie';
import { makeAuthenticatedRequest } from './authService';
import { API_BASE_URL } from '../config/api';

// Convert backend movie data to Movie interface
const backendMovieToMovie = (backendMovie: any): Movie => {
  if (!backendMovie) {
    throw new Error('Backend movie data is null or undefined');
  }
  
  if (!backendMovie.movie_id) {
    throw new Error('Backend movie missing required movie_id field');
  }
  
  const name = backendMovie.name?.trim() || '';
  if (!name) {
    throw new Error('Movie name is required');
  }
  
  const movie: Movie = {
    id: backendMovie.movie_id,
    name: name,
    director: backendMovie.director || undefined,
    year: backendMovie.year || undefined,
    genre: backendMovie.genre || undefined,
    rating: backendMovie.rating || undefined,
    duration: backendMovie.duration || undefined,
    status: backendMovie.status || undefined,
    posterUrl: backendMovie.poster_url || undefined,
    notes: backendMovie.notes || undefined,
    tags: [], // Tags not implemented yet
    dateAdded: backendMovie.created_at || new Date().toISOString(),
    properties: backendMovie.properties || {}
  };
  
  return movie;
};

// Movie service functions
export const movieService = {
  // Get all movies
  async getMovies(): Promise<Movie[]> {
    try {
      const result = await makeAuthenticatedRequest('/movies');
      
      if (result.success && result.data && Array.isArray(result.data.movies)) {
        const movies: Movie[] = [];
        
        for (let i = 0; i < result.data.movies.length; i++) {
          const backendMovie = result.data.movies[i];
          try {
            const convertedMovie = backendMovieToMovie(backendMovie);
            movies.push(convertedMovie);
          } catch (error) {
            console.error(`Error converting movie at index ${i}:`, backendMovie, error);
            continue;
          }
        }
        
        return movies;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching movies:', error);
      return [];
    }
  },

  // Get a specific movie by ID
  async getMovieById(movieId: string): Promise<Movie | null> {
    try {
      const result = await makeAuthenticatedRequest(`/movies/${encodeURIComponent(movieId)}`);
      
      if (result.success && result.data && result.data.movie) {
        return backendMovieToMovie(result.data.movie);
      }
      
      return null;
    } catch (error) {
      console.error('Error getting movie by ID:', error);
      return null;
    }
  },

  // Add a new movie
  async addMovie(movie: Omit<Movie, 'id' | 'dateAdded'>): Promise<Movie | null> {
    try {
      // Generate a unique movie_id
      const movieId = `movie_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const backendMovieData: any = {
        movie_id: movieId,
        name: movie.name,
        director: movie.director,
        year: movie.year,
        genre: movie.genre,
        rating: movie.rating,
        duration: movie.duration,
        status: movie.status,
        poster_url: movie.posterUrl,
        notes: movie.notes,
        properties: movie.properties || {}
      };

      const result = await makeAuthenticatedRequest('/movies', {
        method: 'POST',
        body: JSON.stringify(backendMovieData)
      });
      
      if (result.success && result.data) {
        const newMovie = {
          ...movie,
          id: result.data.movie_id || movieId,
          dateAdded: new Date().toISOString()
        } as Movie;
        
        return newMovie;
      }
      
      return null;
    } catch (error) {
      console.error('Error adding movie:', error);
      return null;
    }
  },

  // Update a movie
  async updateMovie(movie: Movie): Promise<boolean> {
    try {
      const backendMovieData: any = {
        name: movie.name,
        director: movie.director,
        year: movie.year,
        genre: movie.genre,
        rating: movie.rating,
        duration: movie.duration,
        status: movie.status,
        poster_url: movie.posterUrl,
        notes: movie.notes,
        properties: movie.properties || {}
      };

      const result = await makeAuthenticatedRequest(`/movies/${encodeURIComponent(movie.id)}`, {
        method: 'PUT',
        body: JSON.stringify(backendMovieData)
      });
      
      return result.success || false;
    } catch (error) {
      console.error('Error updating movie:', error);
      return false;
    }
  },

  // Delete a movie
  async deleteMovie(movieId: string): Promise<boolean> {
    try {
      const result = await makeAuthenticatedRequest(`/movies/${encodeURIComponent(movieId)}`, {
        method: 'DELETE'
      });
      
      return result.success;
    } catch (error) {
      console.error('Error deleting movie:', error);
      return false;
    }
  },

  // ============================================
  // NOTE LINKING FUNCTIONALITY
  // ============================================

  // Get linked note ID for a movie
  async getMovieNoteId(movieId: string): Promise<string | null> {
    try {
      const movie = await this.getMovieById(movieId);
      if (movie && movie.properties && movie.properties.linkedNoteId) {
        return movie.properties.linkedNoteId;
      }
      return null;
    } catch (error) {
      console.error('Error getting movie note ID:', error);
      return null;
    }
  },

  // Create a note for a movie (lazy creation)
  async createMovieNote(movieId: string, userId: number, movieName: string): Promise<string> {
    try {
      // Import NotesService dynamically to avoid circular dependencies
      const { NotesService } = await import('./notesService');
      
      // Sanitize movie name for note_id
      const sanitizedName = movieName.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const noteId = `movie-${movieId}-${sanitizedName}`;

      // First, try to get the note if it already exists
      try {
        const existingNote = await NotesService.getNoteById(noteId, userId);
        // Note exists, update movie properties if needed and return the note ID
        const movie = await this.getMovieById(movieId);
        if (movie && (!movie.properties || !movie.properties.linkedNoteId)) {
          const currentProperties = movie.properties || {};
          const updatedProperties = {
            ...currentProperties,
            linkedNoteId: existingNote.note_id
          };
          await this.updateMovie({
            ...movie,
            properties: updatedProperties
          });
        }
        return existingNote.note_id;
      } catch (getError: any) {
        // Note doesn't exist yet, continue to create it
        if (getError.status !== 404 && !getError.message?.includes('not found')) {
          // If it's not a "not found" error, rethrow it
          throw getError;
        }
      }

      // Create note with type 'movie' and link to movie
      try {
        const newNote = await NotesService.createNoteWithId(
          userId,
          noteId,
          `${movieName} - Movie Notes`,
          [],
          {
            linkedMovieId: movieId,
            type: 'movie'
          }
        );

        // Update movie properties with linked note_id
        const movie = await this.getMovieById(movieId);
        if (movie) {
          const currentProperties = movie.properties || {};
          const updatedProperties = {
            ...currentProperties,
            linkedNoteId: newNote.note_id
          };

          await this.updateMovie({
            ...movie,
            properties: updatedProperties
          });
        }

        return newNote.note_id;
      } catch (createError: any) {
        // If creation fails due to duplicate entry, try to get the existing note
        if (createError.message?.includes('already exists') || createError.error === 'Validation error') {
          try {
            const existingNote = await NotesService.getNoteById(noteId, userId);
            // Update movie properties if needed
            const movie = await this.getMovieById(movieId);
            if (movie && (!movie.properties || !movie.properties.linkedNoteId)) {
              const currentProperties = movie.properties || {};
              const updatedProperties = {
                ...currentProperties,
                linkedNoteId: existingNote.note_id
              };
              await this.updateMovie({
                ...movie,
                properties: updatedProperties
              });
            }
            return existingNote.note_id;
          } catch (getError) {
            // If we still can't get it, throw the original create error
            throw createError;
          }
        }
        // For other errors, rethrow
        throw createError;
      }
    } catch (error) {
      console.error('Error creating movie note:', error);
      throw error;
    }
  }
};

